import React, { useState } from 'react';
import styled from 'styled-components';
import DOMPurify from 'isomorphic-dompurify';
import { hexToRGB } from 'utils';
import StarRating from './StarRating';

function ReviewCard({ review }) {
  const [showFullText, setShowFullText] = useState(false);
  const maxTextLength = 200;

  const { reviewerName, reviewerPhotoUrl, rating, reviewText, reviewTime, link } = review;

  const formatDate = (dateString) => {
    if (!dateString) return '';
    // For relative dates like "1 month ago", just return as-is
    return dateString;
  };

  const shouldTruncate = reviewText && reviewText.length > maxTextLength;
  const displayText =
    shouldTruncate && !showFullText ? `${reviewText.substring(0, maxTextLength)}...` : reviewText;

  return (
    <Card>
      <ReviewerInfo>
        <ReviewerAvatar>
          {reviewerPhotoUrl ? (
            <ReviewerImage src={reviewerPhotoUrl} alt={reviewerName} />
          ) : (
            <DefaultAvatar>
              {reviewerName ? reviewerName.charAt(0).toUpperCase() : 'A'}
            </DefaultAvatar>
          )}
        </ReviewerAvatar>
        <ReviewerDetails>
          <ReviewerName dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(reviewerName) }} />
          <StarRating rating={rating} size="14px" />
        </ReviewerDetails>
      </ReviewerInfo>

      {reviewText && (
        <ReviewTextContainer>
          <ReviewText>{displayText}</ReviewText>
          {shouldTruncate && (
            <ReadMoreButton href={link} target="_blank">
              {showFullText ? 'Show less' : 'Read more'}
            </ReadMoreButton>
          )}
        </ReviewTextContainer>
      )}
      {reviewTime && <ReviewDate>{formatDate(reviewTime)}</ReviewDate>}
    </Card>
  );
}

const Card = styled.div`
  background-color: ${hexToRGB({ color: '#f1f1f1', alpha: 0.9 })};
  border-radius: 0.375rem;
  color: var(--connolly-black);
  display: flex;
  flex-direction: column;
  gap: 1rem;
  height: 100%;
  margin: 0 auto;
  max-width: 340px;
  padding: 1.5rem;
  width: 100%;

  @media (width >= 430px) {
    margin: unset;
    max-width: 22.5rem;
  }

  @media (width >= 1200px) {
    max-width: 20.5rem;
  }

  @media (width >= 1270px) {
    max-width: 22.5rem;
  }
`;

const ReviewerInfo = styled.div`
  align-items: center;
  display: flex;
  gap: 0.75rem;
`;

const ReviewerAvatar = styled.div`
  flex-shrink: 0;
`;

const ReviewerImage = styled.img`
  border-radius: 50%;
  height: 40px;
  object-fit: cover;
  width: 40px;
`;

const DefaultAvatar = styled.div`
  align-items: center;
  background-color: var(--connolly-red);
  border-radius: 50%;
  color: white;
  display: flex;
  font-size: 1.125rem;
  font-weight: 600;
  height: 40px;
  justify-content: center;
  width: 40px;
`;

const ReviewerDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
`;

const ReviewerName = styled.h4`
  color: var(--connolly-black);
  font-size: 1rem;
  font-weight: 600;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ReviewTextContainer = styled.div`
  flex: 1;
`;

const ReviewText = styled.p`
  color: var(--connolly-black);
  font-family: 'Open Sans', 'Segoe UI', Tahoma, sans-serif;
  font-size: 0.875rem;
  line-height: 1.5;
  margin: 0;
  word-wrap: break-word;
`;

const ReadMoreButton = styled.a`
  background: none;
  border: none;
  color: var(--connolly-red);
  cursor: pointer;
  font-size: 0.8125rem;
  font-weight: 600;
  margin-top: 0.5rem;
  padding: 0;
  text-decoration: underline;

  &:hover {
    text-decoration: none;
  }
`;

const ReviewDate = styled.div`
  color: #666;
  font-size: 0.75rem;
  margin-top: auto;
`;

export default ReviewCard;
